import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { splitEvery, uniq } from 'ramda';
import { useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';

import { searchProducts, createItemConfigurations } from 'apis/product-information-service/api';
import { FieldConfigurations, ItemConfiguration, Product } from 'apis/product-information-service/types';
import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { localize } from 'common/utils';

import { getQueryKeys } from './utils';
import { MenuLocaleContext } from '../MenuLocaleContext';
import { useMenuModuleContext } from '../context';

const CHUNK_SIZE_SEARCH = 1000;
const BATCH_CONNECT_SIZE = 100;

const allItemConfigsWereCreated = (expected: ItemConfiguration[], results: ItemConfiguration[]): boolean => {
  const resultIds = new Set(results.map(res => res.itemId));
  return expected.every(itemConfig => resultIds.has(itemConfig.itemId));
};

// Product catalog uses Gtin14 format
const getGtin14 = (gtin: string | null) => gtin?.padStart(14, '0');

const useConnectItemsToCatalog = (
  items: Item[],
  selectedItemIds: string[],
  ignoredProperties: (keyof FieldConfigurations)[],
  onConnectSuccessCallback: () => void,
) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const { venue } = useMenuModuleContext();
  const { primaryLanguage } = useContext(MenuLocaleContext);

  const [progressPercentage, setProgressPercentage] = useState(0);

  const queryKeys = getQueryKeys(venue);

  const {
    data: listData,
    isLoading: listingData,
    error: listingError,
    remove: resetInitialQuery,
  } = useQuery(
    queryKeys.searchProductsToConnect,
    async () => {
      const gtins = items
        .filter(item => !item.source_config && !!item.barcode_gtin)
        .map(item => item.barcode_gtin)
        .filter((gtin): gtin is string => gtin !== null);
      const uniqueGtins = uniq(gtins);
      const gtinChunks = splitEvery(CHUNK_SIZE_SEARCH, uniqueGtins);
      const searchResults: Product[] = [];

      for (const chunk of gtinChunks) {
        const chunkResult = await searchProducts(venue.country, chunk);
        searchResults.push(...chunkResult);
      }

      const existingGtins = searchResults.map(product => product.gtin);
      const existingGtin14s = existingGtins.map(gtin => getGtin14(gtin));
      const itemsToList = items.filter(
        item =>
          !item.source_config &&
          item.barcode_gtin &&
          (existingGtins?.includes(item.barcode_gtin) || existingGtin14s.includes(item.barcode_gtin)),
      );

      return itemsToList.map(item => ({
        itemId: item.id,
        image: item.image,
        hasProductFamilyId: !!item.product_hierarchy_level_id,
        name: localize(item.name, primaryLanguage),
        gtin: item.barcode_gtin,
      }));
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const {
    mutate,
    isLoading: isConnecting,
    error: connectError,
  } = useMutation(
    async () => {
      if (!venue.menu) {
        throw new Error('Menu is not defined. This should not happen!');
      }

      const allPayloads = selectedItemIds.reduce<ItemConfiguration[]>((acc, currItemId) => {
        const itemToUpdate = items.find(item => item.id === currItemId);
        if (!itemToUpdate || !itemToUpdate.barcode_gtin) {
          return acc;
        } else {
          const fieldConfigurations = ignoredProperties.reduce<FieldConfigurations>(
            (acc, curr) => ({ ...acc, [curr]: 'USER_INPUT' }),
            {},
          );
          const config: ItemConfiguration = {
            itemId: itemToUpdate.id,
            menuId: itemToUpdate.menu_id,
            gtin: itemToUpdate.barcode_gtin,
            country: venue.country,
            fieldConfigurations,
          };
          return [...acc, config];
        }
      }, []);

      const allResults: ItemConfiguration[] = [];

      for (const [index, payload] of splitEvery(BATCH_CONNECT_SIZE, allPayloads).entries()) {
        const percentage = (100 * ((index + 1) * BATCH_CONNECT_SIZE)) / allPayloads.length;
        setProgressPercentage(Math.min(percentage, 100));

        const res = await createItemConfigurations(payload);
        allResults.push(...res);
      }
      return allItemConfigsWereCreated(allPayloads, allResults);
    },
    {
      onSuccess: areAllItemConfigsCreated => {
        const successMessage = intl.formatMessage({ id: 'menu.catalog.connect-items-success' });
        const failureMessage = intl.formatMessage({ id: 'menu.catalog.connect-items-failure' });
        const reloadMessage = intl.formatMessage({ id: 'menu.catalog.connect-items-delay' });
        if (areAllItemConfigsCreated) {
          toast.success(`${successMessage} ${reloadMessage}`, { duration: Infinity });
        }
        if (!areAllItemConfigsCreated) {
          toast.error(`${failureMessage} ${reloadMessage}`, { duration: Infinity });
        }
        resetInitialQuery();
        queryClient.invalidateQueries(queryKeys.searchProductsToAdd);
        onConnectSuccessCallback();
      },
    },
  );

  return {
    listData,
    listingData,
    isConnecting,
    error: listingError || connectError,
    progressPercentage,
    connectItemsToCatalog: mutate,
  };
};

export default useConnectItemsToCatalog;
